import React, {useCallback, useEffect, useState} from 'react';
import Grid from "@mui/material/Grid";
import {TextField, IconButton, Autocomplete} from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import {formValidations} from "../CVTemplate";
import DeleteIcon from '@mui/icons-material/Delete';
import {getSkills} from "../../../utilities/cvAPI";
import keycloak from "../../../Keycloak";

const ExperienceLevel = [
    {
        value: "Novice",
        label: "Novice",
    },
    {
        value: "Skilled",
        label: "Skilled",
    },
    {
        value: "Experienced",
        label: "Experienced",
    },
    {
        value: "Expert",
        label: "Expert",
    },
];
const Current = [
    {
        value: "No",
        label: "No",
    },
    {
        value: "Yes",
        label: "Yes",
    },
];
const YearsOfExperience = [
    {
        value: "0",
        label: "0-1 years",
    },
    {
        value: "1",
        label: "2-4 years",
    },
    {
        value: "2",
        label: "5-7 years",
    },
    {
        value: "3",
        label: "8-10 years",
    },
    {
        value: "4",
        label: "10+ years",
    },
];

export function mapYearsToLevel(years) {
    switch (years) {
        case "0": return "Novice";
        case "1": return "Skilled";
        case "2": return "Experienced";
        case "3":
        case "4": return "Expert";
        default: return "Novice";
    }
}

export const filterOptions = (options, { inputValue }) => {
    return options.filter(option => {
        const skillName = typeof option === 'string' ? option : option.name || "";
        return skillName.toLowerCase().includes(inputValue.toLowerCase());
    }).slice(0, 5);  // Limit the suggestions to 5
};

export default function Skills({ skills, updateCV, userHasClickedNext, readOnly }) {
    const style = {
        "& label.Mui-focused": { color: "#FF6600" },
        "& .MuiOutlinedInput-root": {
            "&.Mui-focused fieldset": { borderColor: "#FF6600" },
        },
    };

    const [formData, setFormData] = useState(skills.length > 0 ? skills : [{ skill: "", level: "", years: "", current: "" }]);
    const [fetchedSkills, setFetchedSkills] = useState([]);

    const handleChange = useCallback((e, index) => {
        const { name, value } = e.target;
        setFormData((prevFormData) => {
            const newFormData = [...prevFormData];
            newFormData[index] = { ...newFormData[index], [name]: value };
            updateCV('skills', newFormData);
            return newFormData;
        });
    }, [updateCV]);

    const checkReqData = useCallback(() => {
        formValidations[4].valid = formData.every(skill =>
            Object.values(skill).every(value => !!value)
        );
    }, [formData]);

    useEffect(() => {
        checkReqData();
    }, [formData, checkReqData]);

    useEffect(() => {
        if (skills.length > 0) {
            const updatedFormData = skills.map(skill => ({
                ...skill,
                level: skill.level || (skill.years ? mapYearsToLevel(skill.years) : skill.level)
            }));
            setFormData(updatedFormData);
        }
    }, [skills]);

    const capitalizeWords = (str) => {
        return str.replace(/(?:^|\s|[^\w])\w/g, (match) => match.toUpperCase());
    };

    const fetchSkills = useCallback(async () => {
        if (keycloak && keycloak.token) {
            try {
                const data = await getSkills(keycloak.token);
                const formattedSkills = data.map(skill => capitalizeWords(skill));
                setFetchedSkills(formattedSkills);
            } catch (err) {
                console.error('Error fetching skills:', err);
                setFetchedSkills([]);
            }
        }
    }, []);

    useEffect(() => {
        fetchSkills();
    }, [fetchSkills]);

    const addSkill = useCallback(() => {
        setFormData(prevFormData => [
            ...prevFormData,
            { skill: "", level: "", years: "", current: "" }
        ]);
    }, []);

    const deleteSkill = useCallback((index) => {
        setFormData(prevFormData => {
            const newFormData = [...prevFormData];
            newFormData.splice(index, 1);
            updateCV('skills', newFormData);
            return newFormData;
        });
    }, [updateCV]);

    return (
        <Grid container direction="row" spacing={2} padding={2} justifyContent="flex-start" alignContent="center">
            <Grid item xs={12}>
                <h3 data-testid="HeadingSkills">Skills:</h3>
            </Grid>
            {formData.map((currentFormData, index) => (
                <Grid container key={index} direction="row" spacing={2} padding={2} justifyContent="flex-start" alignContent="center">
                    <Grid item xs={2.875}>
                        <Autocomplete
                            freeSolo
                            options={fetchedSkills}
                            getOptionLabel={(option) => typeof option === 'string' ? option : option.name || ""}
                            filterOptions={filterOptions}
                            value={currentFormData.skill || ""}
                            onChange={(event, newValue) => handleChange({ target: { name: "skill", value: newValue } }, index)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Skills (Languages/Technologies)"
                                    sx={style}
                                    required
                                    error={userHasClickedNext && !currentFormData.skill}
                                    name="skill"
                                    variant={readOnly ? "standard" : "outlined"}
                                    style={{ width: "99%" }}
                                    onChange={(e) => handleChange(e, index)}
                                    data-testid={`skill-${index}`}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={2.875}>
                        <TextField
                            label="Level of Experience"
                            sx={style}
                            select
                            required
                            error={userHasClickedNext && !currentFormData.level}
                            name="level"
                            variant={readOnly ? "standard" : "outlined"}
                            value={currentFormData.level || ""}
                            onChange={(e) => handleChange(e, index)}
                            data-testid={`skill-level-${index}`}
                            style={{ width: "99%" }}
                        >
                            {ExperienceLevel.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={2.875}>
                        <TextField
                            label="Years Experience"
                            sx={style}
                            select
                            name="years"
                            variant={readOnly ? "standard" : "outlined"}
                            required
                            error={userHasClickedNext && !currentFormData.years}
                            value={currentFormData.years || ""}
                            onChange={(e) => handleChange(e, index)}
                            data-testid={`skill-years-${index}`}
                            style={{ width: "99%" }}
                        >
                            {YearsOfExperience.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={2.875}>
                        <TextField
                            label="Current (Are you using it? Yes/No)"
                            select
                            sx={style}
                            name="current"
                            variant={readOnly ? "standard" : "outlined"}
                            required
                            error={userHasClickedNext && !currentFormData.current}
                            value={currentFormData.current || ""}
                            onChange={(e) => handleChange(e, index)}
                            data-testid={`skill-current-${index}`}
                            style={{ width: "99%" }}
                        >
                            {Current.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={0.5}>
                        {formData.length > 1 ? (
                            <IconButton hidden={readOnly} onClick={() => deleteSkill(index)} style={{ color: '#FF6600', paddingTop: '40%' }} data-testid={`delete-button-${index}`}>
                                <DeleteIcon />
                            </IconButton>
                        ) : (
                            <IconButton hidden={readOnly} disabled={true} style={{ color: '#808080', paddingTop: '40%' }} data-testid={`delete-button-${index}`}>
                                <DeleteIcon />
                            </IconButton>
                        )}
                    </Grid>
                </Grid>
            ))}
            <Grid container direction="row" spacing={2} padding={2} justifyContent="flex-start" alignContent="center">
                <Grid item xs={1.5}>
                    <Button hidden={readOnly} onClick={addSkill} variant="outlined" style={{ width: '89px', height: '40px', fontFamily: 'Poppins', color: '#FF6600', borderColor: '#FF6600' }}>
                        Add
                    </Button>
                </Grid>
            </Grid>
        </Grid>
    );
}